<template>
  <div class="justify-start flex ps-2 pb-2">
    <NText
      weight="medium"
      variant="caption1"
      class="text-neutral-secondary-press"
    >
      {{ t('symbol.marketDepth') }}
    </NText>
  </div>

  <NBox
    class="flex overflow-y-scroll items-start px-3 pb-1 h-[calc(100%-4rem)]"
  >
    <table class="flex-1">
      <colgroup>
        <col class="w-[10%]" />
        <col />
      </colgroup>

      <thead class="text-neutral-secondary">
        <tr>
          <th class="p-1">
            <div class="flex items-center">
              <NText variant="caption2">
                {{ t('misc.quantity') }}
              </NText>
            </div>
          </th>
          <th class="p-1">
            <div class="flex items-center justify-between">
              <NText variant="caption2" class="align-bottom">
                {{ t('symbol.volume') }}
              </NText>
              <NText variant="caption2" class="align-bottom">
                {{ t('symbol.price') }}
              </NText>
            </div>
          </th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="(item, index) of buyMarketDepth" :key="item.price">
          <td class="text-right">
            <NumberFormatter #="{ number }" :value="item.depthModels?.length">
              <NText
                variant="caption2"
                weight="medium"
                :class="{
                  'text-neutral-secondary-disable': isDisabled(item.price),
                }"
              >
                {{ number }}
              </NText>
            </NumberFormatter>
          </td>

          <td class="pr-1">
            <div class="relative bg-transparent" dir="ltr">
              <div
                v-if="!isDisabled(item.price)"
                class="bg-brand-primary darkTheme:bg-opacity-30 bg-opacity-20 h-6 rounded-r-sm transition-all ease-in-out duration-1000"
                :style="{ width: `${progressPercentage(item.volume)}%` }"
              />

              <div
                class="absolute inset-0 top-[0.187rem] flex justify-between items-center px-1"
              >
                <NPopper hover placement="top">
                  <span>
                    <NumberFormatter #="{ number }" :value="item.price">
                      <NText
                        v-gid="{
                          module: 'order',
                          screen: 'home',
                          section: 'marketDepth',
                          component: 'priceSellButton',
                          disabled: isDisabled(item.price),
                        }"
                        variant="caption2"
                        weight="medium"
                        :class="
                          isDisabled(item.price)
                            ? 'text-neutral-secondary-disable'
                            : 'text-brand-primary cursor-pointer'
                        "
                        @click="handleClickPrice(item.price, ORDER_TYPES.sell)"
                      >
                        {{ number }}
                      </NText>
                    </NumberFormatter>
                  </span>

                  <template #content>
                    <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                      <NumberFormatter
                        #="{ number }"
                        :value="
                          calcReferencePriceDifference({ price: item.price })
                        "
                        maximum-fraction-digits="2"
                        minimum-fraction-digits="2"
                        number-style="percent"
                        sign-display="exceptZero"
                      >
                        <NText
                          variant="caption2"
                          weight="medium"
                          class="text-neutral-quaternary"
                          dir="ltr"
                          :class="{
                            '!text-brand-secondary':
                              calcReferencePriceDifference({
                                price: item.price,
                              }) > 0,
                            '!text-critical-secondary':
                              calcReferencePriceDifference({
                                price: item.price,
                              }) < 0,
                          }"
                        >
                          {{ number }}
                        </NText>
                      </NumberFormatter>
                    </div>
                  </template>
                </NPopper>

                <NPopper hover placement="top">
                  <NText
                    :class="{
                      'pointer-events-none': isDisabled(item.price),
                    }"
                  >
                    <NumberFormatter
                      #="{ number }"
                      notation="compact"
                      notation-limit="999999999"
                      minimum-fraction-digits="2"
                      maximum-fraction-digits="2"
                      :value="item.volume"
                    >
                      <NText
                        v-gid="{
                          module: 'order',
                          screen: 'home',
                          section: 'marketDepth',
                          component: 'quantitySellButton',
                          disabled: isDisabled(item.price),
                        }"
                        variant="caption2"
                        weight="medium"
                        :class="
                          isDisabled(item.price)
                            ? 'text-neutral-secondary-disable'
                            : 'cursor-pointer'
                        "
                        @click="
                          handleClickVolume(
                            index,
                            buyValidRange,
                            item.price,
                            ORDER_TYPES.sell
                          )
                        "
                      >
                        {{ number }}
                      </NText>
                    </NumberFormatter>
                  </NText>

                  <template #content>
                    <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                      <NText>
                        <NumberFormatter
                          #="{ number }"
                          :value="calcBuyVolume(index)"
                        >
                          <NText
                            variant="caption2"
                            weight="medium"
                            class="text-neutral-quaternary"
                          >
                            {{ number }}
                          </NText>
                        </NumberFormatter>
                      </NText>
                    </div>
                  </template>
                </NPopper>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <table class="flex-1">
      <colgroup>
        <col />
        <col class="w-[10%]" />
      </colgroup>
      <thead class="text-neutral-secondary">
        <tr>
          <th class="p-1">
            <div class="flex justify-between items-center">
              <NText variant="caption2" class="align-bottom">
                {{ t('symbol.price') }}
              </NText>
              <NText variant="caption2" classs="align-bottom">
                {{ t('symbol.volume') }}
              </NText>
            </div>
          </th>

          <th class="p-1">
            <div class="flex justify-end items-center">
              <NText variant="caption2" class="align-bottom">
                {{ t('misc.quantity') }}
              </NText>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, index) in sellMarketDepth" :key="item.price">
          <td class="pl-1">
            <div class="relative bg-transparent">
              <div
                v-if="!isDisabled(item.price)"
                class="bg-critical-primary darkTheme:bg-opacity-30 bg-opacity-20 h-6 rounded-l-sm transition-all ease-in-out duration-1000"
                :style="{ width: `${progressPercentage(item.volume)}%` }"
              />

              <div
                class="absolute inset-0 top-[0.187rem] flex justify-between items-center px-1"
              >
                <NPopper hover placement="top">
                  <span>
                    <NumberFormatter #="{ number }" :value="item.price">
                      <NText
                        v-gid="{
                          module: 'order',
                          screen: 'home',
                          section: 'marketDepth',
                          component: 'priceBuyButton',
                          disabled: isDisabled(item.price),
                        }"
                        variant="caption2"
                        weight="medium"
                        :class="
                          isDisabled(item.price)
                            ? 'text-neutral-secondary-disable'
                            : 'cursor-pointer text-critical-primary'
                        "
                        @click="handleClickPrice(item.price, ORDER_TYPES.buy)"
                      >
                        {{ number }}
                      </NText>
                    </NumberFormatter>
                  </span>
                  <template #content>
                    <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                      <NumberFormatter
                        #="{ number }"
                        :value="
                          calcReferencePriceDifference({ price: item.price })
                        "
                        maximum-fraction-digits="2"
                        minimum-fraction-digits="2"
                        number-style="percent"
                        sign-display="exceptZero"
                      >
                        <NText
                          variant="caption2"
                          weight="medium"
                          class="text-neutral-quaternary"
                          dir="ltr"
                          :class="{
                            '!text-brand-secondary':
                              calcReferencePriceDifference({
                                price: item.price,
                              }) > 0,
                            '!text-critical-secondary':
                              calcReferencePriceDifference({
                                price: item.price,
                              }) < 0,
                          }"
                        >
                          {{ number }}
                        </NText>
                      </NumberFormatter>
                    </div>
                  </template>
                </NPopper>

                <NPopper hover placement="top">
                  <NText
                    :class="{
                      'pointer-events-none': isDisabled(item.price),
                    }"
                  >
                    <NumberFormatter
                      #="{ number }"
                      notation="compact"
                      notation-limit="999999999"
                      minimum-fraction-digits="2"
                      maximum-fraction-digits="2"
                      :value="item.volume"
                    >
                      <NText
                        v-gid="{
                          module: 'order',
                          screen: 'home',
                          section: 'marketDepth',
                          component: 'quantityBuyButton',
                          disabled: isDisabled(item.price),
                        }"
                        variant="caption2"
                        weight="medium"
                        :class="
                          isDisabled(item.price)
                            ? 'text-neutral-secondary-disable'
                            : 'cursor-pointer'
                        "
                        @click="
                          handleClickVolume(
                            index,
                            sellValidRange,
                            item.price,
                            ORDER_TYPES.buy
                          )
                        "
                      >
                        {{ number }}
                      </NText>
                    </NumberFormatter>
                  </NText>

                  <template #content>
                    <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                      <NumberFormatter
                        #="{ number }"
                        :value="calcSellVolume(index)"
                      >
                        <NText
                          variant="caption2"
                          weight="medium"
                          class="text-neutral-quaternary"
                        >
                          {{ number }}
                        </NText>
                      </NumberFormatter>
                    </div>
                  </template>
                </NPopper>
              </div>
            </div>
          </td>

          <td class="text-left">
            <NumberFormatter #="{ number }" :value="item.depthModels?.length">
              <NText
                variant="caption2"
                weight="medium"
                :class="{
                  'text-neutral-secondary-disable': isDisabled(item.price),
                }"
              >
                {{ number }}
              </NText>
            </NumberFormatter>
          </td>
        </tr>
      </tbody>
    </table>
  </NBox>

  <NBox class="flex my-1 px-2 py-1.5">
    <NPopper
      v-if="totalVolumesPercentage.buy"
      placement="top"
      hover
      offset-y="8"
    >
      <div
        class="bg-brand-primary darkTheme:bg-opacity-30 bg-opacity-20 rounded-r-sm cursor-pointer"
        :class="{ 'rounded-l-sm': !totalVolumesPercentage.sell }"
        :style="{ width: `${totalVolumesPercentage.buy}%` }"
      >
        <NText>
          <NumberFormatter
            #="{ number }"
            maximum-fraction-digits="2"
            number-style="percent"
            :value="totalVolumesPercentage.buy / 100"
          >
            <NText variant="caption2" weight="medium">
              {{ number }}
            </NText>
          </NumberFormatter>
        </NText>
      </div>

      <template #content>
        <div
          class="bg-neutral-quaternary px-2 py-1 rounded-md text-neutral-primary"
          dir="ltr"
        >
          <NumberFormatter
            #="{ number }"
            notation="compact"
            notation-limit="999999"
            maximum-fraction-digits="2"
            :value="totalCount.buy"
          >
            <NText
              variant="caption2"
              weight="medium"
              class="text-neutral-quaternary"
            >
              {{ number }} *
            </NText>
          </NumberFormatter>

          <NumberFormatter
            #="{ number }"
            notation="compact"
            notation-limit="999999"
            maximum-fraction-digits="2"
            :value="totalVolumes.buy"
          >
            <NText
              variant="caption2"
              weight="medium"
              class="text-neutral-quaternary"
            >
              {{ number }}
            </NText>
          </NumberFormatter>
        </div>
      </template>
    </NPopper>

    <NPopper
      v-if="totalVolumesPercentage.sell"
      placement="top"
      hover
      offset-y="8"
    >
      <div
        class="bg-critical-primary darkTheme:bg-opacity-30 bg-opacity-20 rounded-l-sm cursor-pointer mr-0.5"
        :class="{ 'rounded-r-sm': !totalVolumesPercentage.sell }"
        :style="{ width: `${totalVolumesPercentage.sell}%` }"
      >
        <NText>
          <NumberFormatter
            #="{ number }"
            maximum-fraction-digits="2"
            number-style="percent"
            :value="totalVolumesPercentage.sell / 100"
          >
            <NText variant="caption2" weight="medium">
              {{ number }}
            </NText>
          </NumberFormatter>
        </NText>
      </div>

      <template #content>
        <div
          class="bg-neutral-quaternary px-2 py-1 rounded-md text-neutral-primary"
          dir="ltr"
        >
          <NumberFormatter
            #="{ number }"
            notation="compact"
            notation-limit="999999"
            maximum-fraction-digits="2"
            :value="totalCount.sell"
          >
            <NText
              variant="caption2"
              weight="medium"
              class="text-neutral-quaternary"
            >
              {{ number }} *
            </NText>
          </NumberFormatter>

          <NumberFormatter
            #="{ number }"
            notation="compact"
            notation-limit="999999"
            maximum-fraction-digits="2"
            :value="totalVolumes.sell"
          >
            <NText
              variant="caption2"
              weight="medium"
              class="text-neutral-quaternary"
            >
              {{ number }}
            </NText>
          </NumberFormatter>
        </div>
      </template>
    </NPopper>
  </NBox>
</template>

<script setup lang="ts">
import { computed, toRef, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ORDER_TYPES } from '@core/order';
import { MarketDepthItems } from '@libs/api-reference';

import { useLiveSymbolQuery } from '@/modules/symbol';
import { useOrderDialogs } from '@/modules/order';

import NumberFormatter from '@/components/Number.vue';

type MarketDepthTable = {
  buyMarketDepth: MarketDepthItems[];
  sellMarketDepth: MarketDepthItems[];
  lowerPriceThreshold: number;
  upperPriceThreshold: number;
  isin: string;
};

const props = defineProps<MarketDepthTable>();

const { t } = useI18n();

const { openNewOrderFormDialog } = useOrderDialogs();

const { symbol } = useLiveSymbolQuery(toRef(props, 'isin'), {
  immediate: false,
});

const nonDisabledBuyMarketDepth = computed(() =>
  props.buyMarketDepth.filter((item) => !isDisabled(item.price))
);
const nonDisabledSellMarketDepth = computed(() =>
  props.sellMarketDepth.filter((item) => !isDisabled(item.price))
);

const sellValidRange = computed(() => {
  return nonDisabledSellMarketDepth.value.filter(
    (x: MarketDepthItems) => x.volume
  );
});

const buyValidRange = computed(() => {
  return nonDisabledBuyMarketDepth.value.filter(
    (x: MarketDepthItems) => x.volume
  );
});

const maxVolume = computed(() => {
  const maxSellVolume = Math.max.apply(
    Math,
    sellValidRange.value.map((o: MarketDepthItems) => {
      return o.volume;
    })
  );

  const maxBuyVolume = Math.max.apply(
    Math,
    buyValidRange.value.map((o: MarketDepthItems) => {
      return o.volume;
    })
  );

  return Math.max(maxSellVolume, maxBuyVolume);
});

const totalVolumes = computed(() => {
  const totalSellVolume = calcSellVolume(props.sellMarketDepth.length - 1);
  const totalBuyVolume = calcBuyVolume(props.buyMarketDepth.length - 1);

  return {
    sell: totalSellVolume,
    buy: totalBuyVolume,
  };
});
const totalVolumesPercentage = computed(() => {
  const totalVolume = totalVolumes.value.sell + totalVolumes.value.buy;

  return {
    sell: (totalVolumes.value.sell / totalVolume) * 100,
    buy: (totalVolumes.value.buy / totalVolume) * 100,
  };
});
const totalCount = computed(() => ({
  buy:
    nonDisabledBuyMarketDepth.value.reduce(
      (sum, item) => sum + item.count,
      0
    ) ?? 0,
  sell:
    nonDisabledSellMarketDepth.value.reduce(
      (sum, item) => sum + item.count,
      0
    ) ?? 0,
}));

const isDisabled = (item) => {
  return item < props.lowerPriceThreshold || item > props.upperPriceThreshold;
};

const progressPercentage = (item) => {
  return (item / maxVolume.value) * 100;
};

const handleClickPrice = (price, type) => {
  if (!isDisabled(price)) {
    openNewOrderFormDialog({
      type: type,
      price,
      symbol: unref(symbol),
    });
  }
};

const handleClickVolume = (index, property, price, type) => {
  if (!isDisabled(price)) {
    let quantity = 0;
    for (let i = 0; i <= index; i++) {
      quantity += property[i].volume;
    }

    openNewOrderFormDialog({
      type: type,
      price,
      quantity,
      symbol: unref(symbol),
    });
  }
};

const calcBuyVolume = (itemIndex: number) => {
  let quantity = 0;

  for (let i = 0; i <= itemIndex; i++) {
    const item = nonDisabledBuyMarketDepth.value[i];

    if (item) {
      quantity += item.volume;
    }
  }

  return quantity;
};

const calcSellVolume = (itemIndex: number) => {
  let quantity = 0;

  for (let i = 0; i <= itemIndex; i++) {
    const item = nonDisabledSellMarketDepth.value[i];

    if (item) {
      quantity += item.volume;
    }
  }

  return quantity;
};

const calcReferencePriceDifference = ({ price }: { price: number }) => {
  return symbol.value.referencePrice
    ? (price - symbol.value?.referencePrice) / symbol.value?.referencePrice
    : 0;
};
</script>
